/**
 * PhotoSwipe v5 initialization for Dealzbook.ae — Vite entry.
 *
 * Ported from the old hand-copied public/js/photoswipe-init.js (UMD globals) to
 * ESM imports so PhotoSwipe rides the Vite build: content-hashed, Brotli'd,
 * immutable-cached. The CSS travels with this JS entry (imported below), like
 * admin.js. Gallery behaviour is unchanged from the UMD version.
 *
 * Targets #images container with <img> elements.
 * Auto-detects image dimensions (no data-pswp-width/height needed).
 * Supports lazy-loaded images (data-src pattern).
 */
import PhotoSwipe from 'photoswipe';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';

document.addEventListener('DOMContentLoaded', function () {
    var container = document.getElementById('images');
    if (!container) return;

    var imgs = container.querySelectorAll('img');
    if (!imgs.length) return;

    // Read intended source dimensions for the lightbox slot.
    // Prefer HTML width/height attrs (Phase 28: set to 760x1074 — matches
    // source file dimensions). Avoids using naturalWidth/Height when the
    // browser has picked a smaller srcset variant for the thumbnail — those
    // variant dimensions would make PhotoSwipe open the lightbox at the
    // variant's tiny fit size even though src points at the full source.
    // The contentLoad handler below still corrects to actual source dims
    // once the lightbox image loads.
    function readIntendedDims(img) {
        var attrW = parseInt(img.getAttribute('width'), 10);
        var attrH = parseInt(img.getAttribute('height'), 10);
        if (attrW > 0 && attrH > 0) return { width: attrW, height: attrH };
        if (img.naturalWidth > 0 && img.naturalHeight > 0) {
            return { width: img.naturalWidth, height: img.naturalHeight };
        }
        return { width: 0, height: 0 };
    }

    // Build data source array from images
    function buildItems() {
        var items = [];
        imgs.forEach(function (img) {
            // Use data-src (lazy load original) or current src
            var src = img.dataset.src || img.src;
            // Skip placeholder images
            if (src.indexOf('thumb_loading_image') !== -1 || src.indexOf('thumb_missing') !== -1) {
                src = img.dataset.src || src;
            }
            var dims = readIntendedDims(img);
            items.push({
                src: src,
                width: dims.width,
                height: dims.height,
                alt: img.getAttribute('alt') || '',
                element: img
            });
        });
        return items;
    }

    var lightbox = new PhotoSwipeLightbox({
        dataSource: buildItems(),
        pswpModule: PhotoSwipe,
        // UI options
        closeOnVerticalDrag: true,
        pinchToClose: true,
        // Zoom
        initialZoomLevel: 'fit',
        secondaryZoomLevel: 2,
        maxZoomLevel: 4,
        // Animation
        showAnimationDuration: 250,
        hideAnimationDuration: 200,
        // Padding around image
        padding: { top: 20, bottom: 20, left: 20, right: 20 }
    });

    // Fix dimensions for images that haven't fully loaded yet
    lightbox.addFilter('itemData', function (itemData, index) {
        if (!itemData.width || !itemData.height) {
            var img = imgs[index];
            if (img) {
                var dims = readIntendedDims(img);
                if (dims.width > 0 && dims.height > 0) {
                    itemData.width = dims.width;
                    itemData.height = dims.height;
                } else {
                    // Default fallback — will be updated once image loads
                    itemData.width = 1200;
                    itemData.height = 900;
                }
                itemData.src = img.dataset.src || img.src;
            }
        }
        return itemData;
    });

    // Update dimensions once the slide content is loaded
    lightbox.on('contentLoad', function (e) {
        var content = e.content;
        if (content.type === 'image') {
            var imgEl = content.element;
            if (imgEl && imgEl.naturalWidth) {
                content.width = imgEl.naturalWidth;
                content.height = imgEl.naturalHeight;
            }
        }
    });

    lightbox.init();

    // Make images clickable — attach click handlers
    imgs.forEach(function (img, index) {
        img.style.cursor = 'pointer';
        img.addEventListener('click', function (e) {
            e.preventDefault();
            // Rebuild items to get latest dimensions (lazy images may have loaded since init)
            lightbox.options.dataSource = buildItems();
            lightbox.loadAndOpen(index);
        });
    });
});
